/**
 * Foundation Calculator — pure function for plinth-based foundation.
 *
 * Calculates beam count/positions and pier count/positions
 * based on structural spacing rules. All spacing thresholds
 * are configurable via Rule Registry.
 */
import { getRuleValue, type RuleOverrides } from '../../data/ruleRegistry';
import {
    DEFAULT_SITE_CONDITIONS,
    PLINTH_LENGTH_OPTIONS,
    type PlinthLengthMm,
    type SiteConditions,
} from './siteConditions';

// ─── Types ──────────────────────────────────────────────────

export interface BeamPosition {
    /** Beam length in metres */
    length: number;
    /** X position (centre of beam) */
    x: number;
    /** Z position (centre of beam) */
    z: number;
}

export interface PierPosition {
    x: number;
    z: number;
    groundOffsetM: number;
    visibleHeightM: number;
    buriedDepthM: number;
    requiredLengthM: number;
    recommendedPlinthLengthMm: PlinthLengthMm | null;
    status: 'ok' | 'warning';
    /** Legacy Swedish warning string (kept for backward compat) */
    warningReason: string | null;
    /** Translation key — resolves via t[warningReasonKey] when set */
    warningReasonKey: 'domain_found_warningGroundHigh' | 'domain_found_warningPlinthShort' | null;
}

export interface FoundationData {
    numBeams: number;
    numPiersPerBeam: number;
    totalPiers: number;
    /** Total running metres of treated timber for beams */
    totalBeamLengthLm: number;
    beams: BeamPosition[];
    plinths: PierPosition[];
    selectedPlinthLengthMm: PlinthLengthMm;
    minBuriedDepthMm: number;
    baselineVisibleHeightM: number;
    warningCount: number;
}

// ─── Calculator ─────────────────────────────────────────────

/**
 * Calculate foundation layout for a rectangular sauna.
 *
 * @param W - External width (X-axis) in metres
 * @param L - External length (Z-axis) in metres
 */
export function calculateFoundation(
    W: number,
    L: number,
    siteConditions: SiteConditions = DEFAULT_SITE_CONDITIONS,
    overrides: RuleOverrides = {},
): FoundationData {
    const MAX_BEAM_SPACING = getRuleValue<number>('foundation.maxBeamSpacingM', overrides);
    const MAX_PIER_SPACING = getRuleValue<number>('foundation.maxPierSpacingM', overrides);
    const INSET_FRACTION = getRuleValue<number>('foundation.edgeInsetFraction', overrides);
    const MAX_INSET = getRuleValue<number>('foundation.maxEdgeInsetM', overrides);

    const INSET_X = Math.min(MAX_INSET, W * INSET_FRACTION);
    const INSET_Z = Math.min(MAX_INSET, L * INSET_FRACTION);

    const effectiveL = L - 2 * INSET_Z;
    const numBeams = Math.ceil(effectiveL / MAX_BEAM_SPACING) + 1;
    const spacingZ = effectiveL / Math.max(1, numBeams - 1);

    const effectiveW = W - 2 * INSET_X;
    const numPiersPerBeam = Math.ceil(effectiveW / MAX_PIER_SPACING) + 1;
    const spacingX = effectiveW / Math.max(1, numPiersPerBeam - 1);
    const selectedPlinthLengthM = siteConditions.plinthLengthMm / 1000;
    const minBuriedDepthM = siteConditions.minBuriedDepthMm / 1000;
    const baselineVisibleHeightM = Math.max(0, selectedPlinthLengthM - minBuriedDepthM);
    const slopeAcross = siteConditions.terrainMode === 'slope' ? siteConditions.slopeAcrossPercent / 100 : 0;
    const slopeAlong = siteConditions.terrainMode === 'slope' ? siteConditions.slopeAlongPercent / 100 : 0;

    const beams: BeamPosition[] = [];
    const plinths: PierPosition[] = [];

    for (let i = 0; i < numBeams; i++) {
        const zPos = -L / 2 + INSET_Z + i * spacingZ;
        const beamLength = W - 0.05; // slight inset from edges
        beams.push({ length: beamLength, x: 0, z: zPos });

        for (let j = 0; j < numPiersPerBeam; j++) {
            const xPos = -W / 2 + INSET_X + j * spacingX;
            const groundOffsetM = xPos * slopeAcross + zPos * slopeAlong;
            const rawVisibleHeightM = baselineVisibleHeightM - groundOffsetM;
            const visibleHeightM = Math.max(0, rawVisibleHeightM);
            const buriedDepthM = selectedPlinthLengthM - visibleHeightM;
            const requiredLengthM = visibleHeightM + minBuriedDepthM;
            const warningReasons: string[] = [];
            const warningReasonKeys: Array<'domain_found_warningGroundHigh' | 'domain_found_warningPlinthShort'> = [];
            let recommendedPlinthLengthMm: PlinthLengthMm | null = null;

            if (rawVisibleHeightM < 0) {
                warningReasons.push('Marken ligger för högt mot bärlinan.');
                warningReasonKeys.push('domain_found_warningGroundHigh');
            }
            if (buriedDepthM < minBuriedDepthM - 1e-6) {
                warningReasons.push('Vald plintlängd räcker inte för minsta nedgrävning.');
                warningReasonKeys.push('domain_found_warningPlinthShort');
                recommendedPlinthLengthMm =
                    PLINTH_LENGTH_OPTIONS.find((lengthMm) => lengthMm / 1000 >= requiredLengthM) ?? null;
            }

            plinths.push({
                x: xPos,
                z: zPos,
                groundOffsetM,
                visibleHeightM,
                buriedDepthM,
                requiredLengthM,
                recommendedPlinthLengthMm,
                status: warningReasons.length > 0 ? 'warning' : 'ok',
                warningReason: warningReasons.length > 0 ? warningReasons.join(' ') : null,
                warningReasonKey: warningReasonKeys.length > 0 ? warningReasonKeys[0] : null,
            });
        }
    }

    return {
        numBeams,
        numPiersPerBeam,
        totalPiers: numBeams * numPiersPerBeam,
        totalBeamLengthLm: numBeams * (W - 0.05),
        beams,
        plinths,
        selectedPlinthLengthMm: siteConditions.plinthLengthMm,
        minBuriedDepthMm: siteConditions.minBuriedDepthMm,
        baselineVisibleHeightM,
        warningCount: plinths.filter((plinth) => plinth.status === 'warning').length,
    };
}

// ─── Dimension-line metadata for 2D diagram ─────────────────

export interface FoundationDimensionLines {
    /** Total sauna width in mm */
    totalWidthMm: number;
    /** Total sauna length in mm */
    totalLengthMm: number;
    /** Edge inset from sauna boundary to first plinth column (X-axis), mm */
    edgeInsetXMm: number;
    /** Edge inset from sauna boundary to first beam row (Z-axis), mm */
    edgeInsetZMm: number;
    /** Spacing between plinth columns (X-axis), mm — 0 if only 1 column */
    pierSpacingXMm: number;
    /** Spacing between beam rows (Z-axis), mm — 0 if only 1 row */
    beamSpacingZMm: number;
    /** Pier positions in mm, relative to top-left corner (for SVG) */
    piersTopLeft: {
        xMm: number;
        zMm: number;
        label: string;
        groundOffsetMm: number;
        visibleHeightMm: number;
        buriedDepthMm: number;
        requiredLengthMm: number;
        recommendedPlinthLengthMm: PlinthLengthMm | null;
        status: 'ok' | 'warning';
        warningReason: string | null;
        warningReasonKey: 'domain_found_warningGroundHigh' | 'domain_found_warningPlinthShort' | null;
    }[];
    /** Beam positions in mm, relative to top-left corner (for SVG) */
    beamsTopLeft: { zMm: number; lengthMm: number }[];
}

/**
 * Transform foundation data from centre-origin metres
 * to top-left-origin millimetres for 2D SVG rendering.
 */
export function computeFoundationDimensionLines(
    foundation: FoundationData,
    W: number,
    L: number,
): FoundationDimensionLines {
    const totalWidthMm = Math.round(W * 1000);
    const totalLengthMm = Math.round(L * 1000);

    // Convert centre-origin → top-left-origin (TL = 0,0)
    const halfW = W / 2;
    const halfL = L / 2;

    const piersTopLeft = foundation.plinths.map((p, idx) => {
        const col = idx % foundation.numPiersPerBeam;
        const row = Math.floor(idx / foundation.numPiersPerBeam);
        const rowLabel = String.fromCharCode(65 + row); // A, B, C…
        return {
            xMm: Math.round((p.x + halfW) * 1000),
            zMm: Math.round((p.z + halfL) * 1000),
            label: `${rowLabel}${col + 1}`,
            groundOffsetMm: Math.round(p.groundOffsetM * 1000),
            visibleHeightMm: Math.round(p.visibleHeightM * 1000),
            buriedDepthMm: Math.round(p.buriedDepthM * 1000),
            requiredLengthMm: Math.round(p.requiredLengthM * 1000),
            recommendedPlinthLengthMm: p.recommendedPlinthLengthMm,
            status: p.status,
            warningReason: p.warningReason,
            warningReasonKey: p.warningReasonKey,
        };
    });

    const beamsTopLeft = foundation.beams.map((b) => ({
        zMm: Math.round((b.z + halfL) * 1000),
        lengthMm: Math.round(b.length * 1000),
    }));

    // Compute spacings from the first two positions
    let edgeInsetXMm = 0;
    let edgeInsetZMm = 0;
    let pierSpacingXMm = 0;
    let beamSpacingZMm = 0;

    if (piersTopLeft.length > 0) {
        edgeInsetXMm = piersTopLeft[0].xMm;
        edgeInsetZMm = piersTopLeft[0].zMm;
    }

    if (foundation.numPiersPerBeam > 1 && piersTopLeft.length >= 2) {
        pierSpacingXMm = piersTopLeft[1].xMm - piersTopLeft[0].xMm;
    }

    if (foundation.numBeams > 1 && beamsTopLeft.length >= 2) {
        beamSpacingZMm = beamsTopLeft[1].zMm - beamsTopLeft[0].zMm;
    }

    return {
        totalWidthMm,
        totalLengthMm,
        edgeInsetXMm,
        edgeInsetZMm,
        pierSpacingXMm,
        beamSpacingZMm,
        piersTopLeft,
        beamsTopLeft,
    };
}
